import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Copy,
  FolderSearch,
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useMutation, useQuery } from '../../gql/hooks';
import {
  PROBE_PROVIDER_MUTATION,
  PROVIDER_UPSTREAM_MODELS_QUERY,
  type ProbeProviderMutationResult,
  type ProbeProviderMutationVariables,
  type ProviderUpstreamModelsResult,
  type ProviderUpstreamModelsVariables,
} from '../../gql/operations';
import { useTheme } from '../../context/ThemeContext';
import { Button, Modal } from '../../ui';
import { InlineBanner } from '../shared/InlineBanner';
import { formatRelativeCompact } from '../../lib/format';

// 2026-07-05 — "Browse upstream models" nested dialog on the provider edit modal.
//
// Reads the ModelListCache-backed view of what the upstream is currently serving,
// cross-referenced against the provider's own enabled aliases. Cache-only from
// GraphQL — if the cache is cold (no successful probe yet) OR the operator hits
// Refresh, we fire `probeProvider(id)` to warm it and re-run the query. This keeps
// the query itself side-effect-free.
//
// Layered above the ProviderFormModal via the shared Modal primitive (portalled to
// document.body). React portals stack in insertion order, so the nested modal
// renders on top and its own backdrop click closes only it — the outer edit form
// stays open beneath.

interface ProbedIndicatorProps {
  probedAt: string | null;
  loading: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}

function ProbedIndicator({ probedAt, loading, onRefresh, refreshing }: ProbedIndicatorProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const label = useMemo(() => {
    if (loading && !probedAt) return 'probing…';
    if (!probedAt) return 'never probed';
    return `probed ${formatRelativeCompact(probedAt)}`;
  }, [loading, probedAt]);

  return (
    <div
      className={`inline-flex items-center gap-2 text-xs ${
        isDark ? 'text-white/50' : 'text-slate-500'
      }`}
    >
      {probedAt ? (
        <Wifi
          className={`h-3.5 w-3.5 ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}
          aria-hidden="true"
        />
      ) : (
        <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      <span>{label}</span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
          isDark
            ? 'border-white/[0.08] bg-white/[0.02] text-white/70 hover:bg-white/[0.05] disabled:opacity-40'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40'
        }`}
        aria-label="Refresh upstream model list"
      >
        <RefreshCw
          className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`}
          aria-hidden="true"
        />
        Refresh
      </button>
    </div>
  );
}

interface UpstreamModelRowProps {
  id: string;
  aliasedAs: string[];
  onCopy: (id: string) => void;
  copiedId: string | null;
}

function UpstreamModelRow({ id, aliasedAs, onCopy, copiedId }: UpstreamModelRowProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isAliased = aliasedAs.length > 0;
  const wasJustCopied = copiedId === id;

  return (
    <li
      className={`group flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors ${
        isDark
          ? 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]'
          : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
      data-testid={`upstream-model-${id}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <code
          className={`truncate font-mono text-xs ${isDark ? 'text-white/85' : 'text-slate-800'}`}
          title={id}
        >
          {id}
        </code>
        {isAliased && (
          <span
            className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
              isDark
                ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
            title={`Aliased for ${aliasedAs.join(', ')}`}
          >
            <Check className="h-2.5 w-2.5" aria-hidden="true" />
            aliased{aliasedAs.length > 1 ? ` (${aliasedAs.length})` : ''}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => onCopy(id)}
        className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium opacity-0 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 group-hover:opacity-100 focus-visible:opacity-100 ${
          isDark
            ? 'border-white/[0.08] bg-white/[0.02] text-white/70 hover:bg-white/[0.05]'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
        }`}
        aria-label={`Copy ${id} to clipboard`}
      >
        {wasJustCopied ? (
          <>
            <Check className="h-3 w-3" aria-hidden="true" /> copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" aria-hidden="true" /> copy
          </>
        )}
      </button>
    </li>
  );
}

export interface BrowseUpstreamModelsModalProps {
  isOpen: boolean;
  providerId: string | null;
  providerName: string | null;
  onClose: () => void;
}

export function BrowseUpstreamModelsModal({
  isOpen,
  providerId,
  providerName,
  onClose,
}: BrowseUpstreamModelsModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // The GraphQL hook doesn't skip a query without an id, so gate it via `enabled`.
  const query = useQuery<ProviderUpstreamModelsResult, ProviderUpstreamModelsVariables>(
    PROVIDER_UPSTREAM_MODELS_QUERY,
    { id: providerId ?? '' },
    { enabled: isOpen && !!providerId },
  );

  const probe = useMutation<ProbeProviderMutationResult, ProbeProviderMutationVariables>(
    PROBE_PROVIDER_MUTATION,
  );

  // If the modal opens and the cache is cold (no probedAt on the returned payload),
  // fire a probe once to warm the cache — otherwise the operator opens the dialog and
  // immediately sees "0 models" which is confusing on a healthy provider.
  const [autoProbeFired, setAutoProbeFired] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setAutoProbeFired(false);
      return;
    }
    if (autoProbeFired) return;
    if (!providerId || query.loading) return;
    const payload = query.data?.providerUpstreamModels;
    if (payload && payload.probedAt == null && payload.models.length === 0) {
      setAutoProbeFired(true);
      void probe.mutate({ id: providerId }).then(() => query.refetch());
    }
  }, [isOpen, autoProbeFired, providerId, query, probe]);

  const handleCopy = useCallback((id: string) => {
    void navigator.clipboard?.writeText(id).then(() => {
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 1500);
    });
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!providerId) return;
    await probe.mutate({ id: providerId });
    query.refetch();
  }, [providerId, probe, query]);

  const models = query.data?.providerUpstreamModels?.models ?? [];
  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return models;
    return models.filter((m) => m.id.toLowerCase().includes(needle));
  }, [models, filter]);

  const probedAt = query.data?.providerUpstreamModels?.probedAt ?? null;
  const busy = query.loading || probe.loading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upstream models"
      subtitle={
        providerName
          ? `Live view of what ${providerName} is serving via /v1/models`
          : 'Live view of what this provider is serving via /v1/models'
      }
      icon={<FolderSearch className="h-7 w-7" aria-hidden="true" />}
      footer={
        <Button variant="secondary" fullWidth onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4" data-testid="browse-upstream-models">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ProbedIndicator
            probedAt={probedAt}
            loading={query.loading}
            onRefresh={() => void handleRefresh()}
            refreshing={probe.loading}
          />
          <span
            className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}
          >
            {models.length} model{models.length === 1 ? '' : 's'}
          </span>
        </div>

        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by id…"
          className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:ring-2 focus:ring-indigo-500/40 ${
            isDark
              ? 'border-white/[0.08] bg-white/[0.02] text-white placeholder:text-white/30'
              : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
          }`}
          aria-label="Filter upstream models"
        />

        {query.error && (
          <InlineBanner tone="error" testId="upstream-models-error">
            Couldn't load the model list: {query.error.message}
          </InlineBanner>
        )}
        {probe.error && (
          <InlineBanner tone="error">
            The refresh probe failed: {probe.error.message}
          </InlineBanner>
        )}

        {busy && models.length === 0 && (
          <div
            className={`flex items-center justify-center gap-2 rounded-lg border py-8 text-sm ${
              isDark
                ? 'border-white/[0.06] bg-white/[0.02] text-white/60'
                : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>Fetching the upstream model list…</span>
          </div>
        )}

        {!busy && models.length === 0 && !query.error && (
          <div
            className={`rounded-lg border py-10 text-center text-sm ${
              isDark
                ? 'border-white/[0.06] bg-white/[0.02] text-white/60'
                : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}
          >
            No models cached. Save the provider config first, then click Refresh —
            the probe will populate this list.
          </div>
        )}

        {filtered.length > 0 && (
          <ul className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
            {filtered.map((m) => (
              <UpstreamModelRow
                key={m.id}
                id={m.id}
                aliasedAs={m.aliasedAs}
                onCopy={handleCopy}
                copiedId={copiedId}
              />
            ))}
          </ul>
        )}

        {filtered.length === 0 && models.length > 0 && (
          <div
            className={`text-center text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}
          >
            No models match the filter.
          </div>
        )}
      </div>
    </Modal>
  );
}
